import {
  Card,
  Snackbar,
  Typography,
  useTheme,
  Button,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useState, useRef } from 'react';
import {
  useUploadProofOfOwnershipForContact,
  useUploadProofOfOwnershipForSendPreview,
  useUploadProofOfOwnershipForTransaction,
} from './api';

interface ProofOfOwnershipProps {
  contactId?: string;
  transactionId?: string;
  sendOrderPreviewId?: string;
}
export const ProofOfOwnership = ({
  contactId,
  transactionId,
  sendOrderPreviewId,
}: ProofOfOwnershipProps) => {
  const { spacing, palette } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { mutate: uploadForContact, isPending: isUploadingForContact } =
    useUploadProofOfOwnershipForContact();
  const { mutate: uploadForTransaction, isPending: isUploadingForTransaction } =
    useUploadProofOfOwnershipForTransaction();
  const { mutate: uploadForSendPreview, isPending: isUploadingForSendPreview } =
    useUploadProofOfOwnershipForSendPreview();

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  const MAX_FILES = 10;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);

      // Check file count limit
      if (selectedFiles.length + newFiles.length > MAX_FILES) {
        setUploadError(`You can only upload a maximum of ${MAX_FILES} files.`);
        return;
      }

      // Check file size limit
      const oversizedFiles = newFiles.filter(
        (file) => file.size > MAX_FILE_SIZE
      );
      if (oversizedFiles.length > 0) {
        setUploadError(
          `The following file(s) exceed the 10MB limit: ${oversizedFiles
            .map((f) => f.name)
            .join(', ')}`
        );
        return;
      }

      setUploadError(null);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadForTransaction = () => {
    if (!transactionId || selectedFiles.length === 0) return;

    setUploadError(null);

    uploadForTransaction(
      { transactionId, files: selectedFiles },
      {
        onSuccess: () => {
          setUploadSuccess(true);
          setSelectedFiles([]);
        },
        onError: (error) => {
          setUploadError(
            error instanceof Error
              ? error.message
              : 'Upload failed. Please try again.'
          );
        },
      }
    );
  };

  const handleUploadForContact = () => {
    if (!contactId || selectedFiles.length === 0) return;

    setUploadError(null);

    uploadForContact(
      { contactId, files: selectedFiles },
      {
        onSuccess: () => {
          setUploadSuccess(true);
          setSelectedFiles([]);
        },
        onError: (error) => {
          setUploadError(
            error instanceof Error
              ? error.message
              : 'Upload failed. Please try again.'
          );
        },
      }
    );
  };

  const handleUploadForSendPreview = () => {
    if (!sendOrderPreviewId || selectedFiles.length === 0) return;

    setUploadError(null);

    uploadForSendPreview(
      { sendOrderPreviewId, files: selectedFiles },
      {
        onSuccess: () => {
          setUploadSuccess(true);
          setSelectedFiles([]);
        },
        onError: (error) => {
          setUploadError(
            error instanceof Error
              ? error.message
              : 'Upload failed. Please try again.'
          );
        },
      }
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div
      css={{
        padding: spacing.xs,
      }}
    >
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          gap: spacing.xs,
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        {uploadError && (
          <div>
            <Snackbar showAsBanner variant="danger" message={uploadError} />
          </div>
        )}
        <Card
          css={{
            maxWidth: '600px',
            padding: spacing.m,
            textAlign: 'center',
          }}
        >
          {uploadSuccess ? (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xs,
                alignItems: 'center',
              }}
            >
              <Typography variant="heading1">
                Documents Uploaded Successfully!
              </Typography>
              <div>
                <Typography variant="body">
                  Thank you for submitting your proof of ownership documents.
                </Typography>
              </div>
              <div>
                <Typography variant="body">
                  Your documents will be reviewed by our team. This process
                  typically takes 1-2 business days. You will be notified once
                  the review is complete.
                </Typography>
              </div>
              <div css={{ marginTop: spacing.s }}>
                <Typography variant="bodySmall">
                  You can safely close this window now.
                </Typography>
              </div>
            </div>
          ) : (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xxs,
                alignItems: 'center',
              }}
            >
              <Typography variant="heading1">
                Final Step: Proof of Ownership
              </Typography>
              <div>
                <Typography variant="body">
                  As a final step, please provide proof of ownership for the
                  wallet.
                </Typography>
              </div>
              <div>
                <Typography variant="body">
                  Please attach screenshots from your wallet and/or
                  transactions. Any additional material that you send will
                  improve the likelihood of the proof being accepted. This will
                  result in a much faster experience when sending to this
                  wallet, bypassing some monitoring rules that might have placed
                  the transaction under manual review.
                </Typography>
              </div>

              <div
                css={{
                  marginTop: spacing.m,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.s,
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  css={{ display: 'none' }}
                />

                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={
                    isUploadingForContact ||
                    isUploadingForTransaction ||
                    isUploadingForSendPreview ||
                    selectedFiles.length >= MAX_FILES
                  }
                  css={{ width: '100%' }}
                  text="Select Files"
                />

                <div
                  css={{
                    padding: spacing.xxxs,
                    borderRadius: '8px',
                    backgroundColor: palette.grey.x50,
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="bodySmall">
                    Maximum {MAX_FILES} files, 10MB each
                  </Typography>
                </div>

                {selectedFiles.length > 0 && (
                  <div
                    css={{
                      marginTop: spacing.xs,
                      padding: spacing.s,
                      borderRadius: '8px',
                      backgroundColor: palette.grey.x50,
                      textAlign: 'left',
                    }}
                  >
                    <div css={{ marginBottom: spacing.xxs }}>
                      <Typography variant="bodySmall">
                        Selected files ({selectedFiles.length}):
                      </Typography>
                    </div>
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: spacing.xxs,
                          marginBottom: spacing.xxs,
                          backgroundColor: palette.white,
                          borderRadius: '4px',
                        }}
                      >
                        <div css={{ flex: 1, minWidth: 0 }}>
                          <div
                            css={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Typography variant="bodySmall">
                              {file.name}
                            </Typography>
                          </div>
                          <div css={{ color: palette.grey.default }}>
                            <Typography variant="bodySmall">
                              {formatFileSize(file.size)}
                            </Typography>
                          </div>
                        </div>
                        <UtilityButton
                          variant="icon"
                          onClick={() => handleRemoveFile(index)}
                          disabled={
                            isUploadingForContact ||
                            isUploadingForTransaction ||
                            isUploadingForSendPreview
                          }
                          icon="trash"
                        />
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  variant="primary"
                  onClick={() => {
                    handleUploadForContact();
                    handleUploadForTransaction();
                    handleUploadForSendPreview();
                  }}
                  disabled={
                    selectedFiles.length === 0 ||
                    isUploadingForContact ||
                    isUploadingForTransaction ||
                    isUploadingForSendPreview
                  }
                  loading={
                    isUploadingForContact ||
                    isUploadingForTransaction ||
                    isUploadingForSendPreview
                  }
                  css={{ width: '100%', marginTop: spacing.xs }}
                  text={
                    isUploadingForContact ||
                    isUploadingForTransaction ||
                    isUploadingForSendPreview
                      ? 'Uploading...'
                      : 'Upload Documents'
                  }
                />
              </div>
            </div>
          )}
        </Card>
      </div>
      <div id="nb-withdrawal" css={{ visibility: 'hidden' }} />
    </div>
  );
};
